import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, Rocket, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

interface CheckInData {
  lastCheckIn: string;
  streak: number;
  totalCheckIns: number;
}

const motivationalQuotes = [
  "Success is not final, failure is not fatal: it is the courage to continue that counts.",
  "The only way to do great work is to love what you do.",
  "Believe you can and you're halfway there.",
  "Don't watch the clock; do what it does. Keep going.",
  "The future depends on what you do today.",
  "Success usually comes to those who are too busy to be looking for it.",
  "Opportunities don't happen, you create them.",
  "The harder you work for something, the greater you'll feel when you achieve it.",
  "Dream bigger. Do bigger.",
  "Don't stop when you're tired. Stop when you're done.",
  "Your limitation—it's only your imagination.",
  "Great things never come from comfort zones.",
  "Success doesn't just find you. You have to go out and get it.",
  "The key to success is to focus on goals, not obstacles.",
  "Dream it. Wish it. Do it.",
  "Push yourself, because no one else is going to do it for you.",
  "Sometimes later becomes never. Do it now.",
  "The secret of getting ahead is getting started.",
  "It's going to be hard, but hard does not mean impossible.",
  "Don't wait for opportunity. Create it.",
  "Your passion is waiting for your courage to catch up.",
  "Motivation is what gets you started. Habit is what keeps you going.",
  "Small steps every day lead to big results.",
  "Focus on the step in front of you, not the whole staircase.",
  "Every accomplishment starts with the decision to try.",
  "The best time to plant a tree was 20 years ago. The second best time is now.",
  "You don't have to be great to start, but you have to start to be great.",
  "The only impossible journey is the one you never begin.",
  "Your only limit is you.",
  "Consistency is what transforms average into excellence.",
  "Build your empire one brick at a time.",
];

const LaunchTracker = () => {
  const navigate = useNavigate();
  const [checkInData, setCheckInData] = useState<CheckInData>({
    lastCheckIn: "",
    streak: 0,
    totalCheckIns: 0,
  });
  const [hasCheckedInToday, setHasCheckedInToday] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  // Get quote of the day based on current date
  const getDailyQuote = () => {
    const today = new Date();
    const dayOfYear = Math.floor(
      (today.getTime() - new Date(today.getFullYear(), 0, 0).getTime()) / 86400000
    );
    return motivationalQuotes[dayOfYear % motivationalQuotes.length];
  };

  // Load user and check-in data
  useEffect(() => {
    const loadUserAndData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          setIsLoading(false);
          return;
        }

        setUserId(user.id);
        
        // Fetch latest check-in from database
        const { data: checkins, error } = await supabase
          .from("launch_tracker_checkins")
          .select("*")
          .eq("user_id", user.id)
          .order("check_in_date", { ascending: false })
          .limit(1);

        if (error) {
          console.error("Error loading check-ins:", error);
          toast.error("Failed to load check-in history");
        } else if (checkins && checkins.length > 0) {
          const latestCheckIn = checkins[0];
          setCheckInData({
            lastCheckIn: new Date(latestCheckIn.check_in_date).toDateString(),
            streak: latestCheckIn.streak,
            totalCheckIns: latestCheckIn.total_checkins,
          });
        } else {
          // Check if there's legacy localStorage data to migrate
          const saved = localStorage.getItem("launchTrackerData");
          if (saved) {
            try {
              const legacyData = JSON.parse(saved);
              setCheckInData(legacyData);
              // Migrate to database
              if (legacyData.lastCheckIn) {
                const lastCheckInDate = new Date(legacyData.lastCheckIn);
                await supabase.from("launch_tracker_checkins").insert({
                  user_id: user.id,
                  check_in_date: lastCheckInDate.toISOString().split('T')[0],
                  streak: legacyData.streak || 1,
                  total_checkins: legacyData.totalCheckIns || 1,
                });
                localStorage.removeItem("launchTrackerData");
              }
            } catch (e) {
              console.error("Error migrating localStorage data:", e);
            }
          }
        }
      } catch (error) {
        console.error("Error in loadUserAndData:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserAndData();
  }, []);

  useEffect(() => {
    const today = new Date().toDateString();
    setHasCheckedInToday(checkInData.lastCheckIn === today);
  }, [checkInData.lastCheckIn]);

  const handleCheckIn = async () => {
    if (!userId) {
      toast.error("Please sign in to check in");
      navigate("/auth");
      return;
    }

    const today = new Date();
    const todayString = today.toDateString();
    
    if (hasCheckedInToday) {
      toast.info("You've already checked in today! See you tomorrow! 🚀");
      return;
    }

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayString = yesterday.toDateString();

    let newStreak = 1;
    if (checkInData.lastCheckIn === yesterdayString) {
      // Continue streak
      newStreak = checkInData.streak + 1;
    } else if (checkInData.lastCheckIn !== todayString) {
      // Streak broken, start new
      newStreak = 1;
    }

    const newTotalCheckIns = checkInData.totalCheckIns + 1;

    try {
      // Save to database
      const { error } = await supabase
        .from("launch_tracker_checkins")
        .insert({
          user_id: userId,
          check_in_date: today.toISOString().split('T')[0],
          streak: newStreak,
          total_checkins: newTotalCheckIns,
        });

      if (error) {
        console.error("Error saving check-in:", error);
        toast.error("Failed to save check-in. Please try again.");
        return;
      }

      const newData: CheckInData = {
        lastCheckIn: todayString,
        streak: newStreak,
        totalCheckIns: newTotalCheckIns,
      };

      setCheckInData(newData);
      setHasCheckedInToday(true);

      if (newStreak > 1) {
        toast.success(`Amazing! ${newStreak} day streak! Keep the momentum going! 🔥`, {
          duration: 4000,
        });
      } else {
        toast.success("Check-in complete! Let's make today count! 💪", {
          duration: 3000,
        });
      }
    } catch (error) {
      console.error("Error in handleCheckIn:", error);
      toast.error("An error occurred. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <section className="w-full">
        <Card className="overflow-hidden border-2">
          <CardContent className="pt-6">
            <div className="text-center text-muted-foreground">Loading...</div>
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <section className="w-full">
      <Card className="overflow-hidden border-2 hover:border-primary/50 transition-colors">
        <CardHeader className="bg-gradient-to-br from-primary/10 to-accent/10 pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl font-bold flex items-center gap-2">
              <Rocket className="w-6 h-6 text-primary" />
              Daily Launch Tracker
            </CardTitle>
            {checkInData.streak > 0 && (
              <div className="flex items-center gap-2 bg-primary/20 px-4 py-2 rounded-full">
                <TrendingUp className="w-5 h-5 text-primary" />
                <span className="font-bold text-lg">{checkInData.streak} day{checkInData.streak !== 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-6">
            {/* Daily Quote */}
            <div className="relative">
              <div className="absolute -left-2 top-0 text-6xl text-primary/20 font-serif">"</div>
              <p className="text-lg md:text-xl font-medium text-center italic px-8 py-4">
                {getDailyQuote()}
              </p>
              <div className="absolute -right-2 bottom-0 text-6xl text-primary/20 font-serif">"</div>
            </div>

            {/* Check-in Button and Stats */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t">
              <div className="flex gap-6 text-sm">
                <div className="text-center">
                  <div className="font-bold text-2xl text-primary">{checkInData.streak}</div>
                  <div className="text-muted-foreground">Current Streak</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-2xl text-primary">{checkInData.totalCheckIns}</div>
                  <div className="text-muted-foreground">Total Check-ins</div>
                </div>
              </div>
              
              <Button
                size="lg"
                onClick={handleCheckIn}
                disabled={hasCheckedInToday}
                className="gap-2 min-w-[200px]"
              >
                {hasCheckedInToday ? (
                  <>
                    <CheckCircle2 className="w-5 h-5" />
                    Checked In Today
                  </>
                ) : (
                  <>
                    <Rocket className="w-5 h-5" />
                    Check In Now
                  </>
                )}
              </Button>
            </div>

            {hasCheckedInToday && (
              <div className="text-center text-sm text-muted-foreground animate-fade-in">
                Great job! Come back tomorrow to continue your streak 🎯
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </section>
  );
};

export default LaunchTracker;
